import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectIsAuthenticated, selectUser } from "../auth/authSelectors";
import { createOrder, verifyPayment, validateWaiverCode } from "../api/paymentService";
import {
  FaTrash, FaListAlt, FaUsers, FaInfoCircle, 
  FaChevronRight, FaTicketAlt, FaShieldAlt, FaCheckCircle
} from "react-icons/fa";

export default function TournamentRegistration() {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector(selectUser);
  const token = useSelector((state) => state.auth.access_token);

  // --- API DATA MAPPING ---
  const tournament = location.state?.tournament;
  const entryFee = parseFloat(tournament?.entry_fee || 0);
  const additionalFee = parseFloat(tournament?.additional_fee || 0);
  const totalFeePerPerson = parseFloat(tournament?.total_fee || 0);
  const additionalFeeLabel = tournament?.additional_fee_label || "CDCA Fee";
  const tournamentId = tournament?.id || null;
  const categories = tournament?.playing_categories || [];

  // --- STATE ---
  const [participant, setParticipant] = useState({ first_name: "", initial: "", dob: "", category: "" });
  const [participantsList, setParticipantsList] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState({ text: "", type: "" });

  // Waiver State
  const [couponCode, setCouponCode] = useState("");
  const [discountAmount, setDiscountAmount] = useState(0);
  const [isCouponApplied, setIsCouponApplied] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  useEffect(() => {
    if (isAuthenticated === false) navigate("/login");
    if (!tournament) navigate("/tournaments");
  }, [isAuthenticated, navigate, tournament]);

  // --- HANDLERS ---
  const handleInput = (e) => setParticipant({ ...participant, [e.target.name]: e.target.value });

  const handleAddParticipant = (e) => {
    e.preventDefault();
    if (!participant.first_name || !participant.category) return;
    setParticipantsList([...participantsList, participant]);
    setParticipant({ first_name: "", initial: "", dob: "", category: "" });
    if (isCouponApplied) handleRemoveWaiver(); 
  };

  const handleRemoveParticipant = (index) => {
    setParticipantsList(participantsList.filter((_, idx) => idx !== index));
    handleRemoveWaiver();
  };

  const loadRazorpay = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};


  const handleApplyDiscount = async () => {
    if (!couponCode.trim() || participantsList.length === 0) return;
    setIsValidating(true);
    try {
      const res = await validateWaiverCode(token, {
        code: couponCode,
        tournament_id: tournamentId,
        count: participantsList.length
      });
      if (res.status === "success") {
        setDiscountAmount(parseFloat(res.discount_amount)); 
        setIsCouponApplied(true);
        setStatusMessage({ text: res.message, type: "success" });
      } else {
        setStatusMessage({ text: res.message, type: "danger" });
      }
    } catch (err) {
      setStatusMessage({ text: "Error validating code", type: "danger" });
    } finally {
      setIsValidating(false);
    }
  };

  const handleRemoveWaiver = () => {
    setIsCouponApplied(false);
    setCouponCode("");
    setDiscountAmount(0);
  };

  // --- CALCULATIONS ---
  const rawSubtotal = participantsList.length * totalFeePerPerson;
  const finalTotal = Math.max(0, rawSubtotal - discountAmount);
  const handlePayment = async () => {
  setIsSubmitting(true);
     const res = await loadRazorpay();
    if (!res) throw new Error("Razorpay SDK failed to load");
  try {
    const orderData = {
      tournament_id: tournamentId,
      participants: participantsList,
      total_amount: finalTotal,
      waiver_code: isCouponApplied ? couponCode : null
    };

    const data = await createOrder(token, orderData);
     const mobileNumber = data.mobile || user?.mobile || "";
    console.log("ORDER RESPONSE:", data);

    if (!data?.order?.razorpay_order_id) {
      throw new Error("Razorpay order id missing");
    }

    const options = {
      key: "rzp_live_Rg1tGOHDjKtxco",
      amount: data.order.amount * 100,
      currency: "INR",
      name: "Tournament Portal",
      description: "Tournament Registration Fee",
      order_id: data.order.razorpay_order_id,

      handler: async (response) => {
        console.log("PAYMENT RESPONSE:", response);

        const verify = await verifyPayment(token, {
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature
        });

        if (verify.status === "success") {
            navigate("/payment_status", {
            state: {
              status: verify.status === "success" ? "success" : "failed",
              message:
                verify.status === "success"
                  ? "Payment successful!"
                  : "Payment verification failed!",
              amount: data.order.amount,
              orderId: tournamentId,
              razorpayPaymentId: response.razorpay_payment_id,
              mobile: mobileNumber
            }
          });
        } else {
          setStatusMessage({ text: "Payment verification failed", type: "danger" });
        }
      },

      prefill: {
        name: user?.name,
        email: user?.email,
        contact: data.mobile
      },

      theme: { color: "#0F172A" }
    };

    const rzp = new window.Razorpay(options);
    rzp.open();

  } catch (err) {
    console.error("PAYMENT ERROR:", err);
    setStatusMessage({ text: "Payment Error", type: "danger" });
  } finally {
    setIsSubmitting(false);
  }
};



  // const handlePayment = async () => {
  //   setIsSubmitting(true);
  //   try {
  //     const orderData = { tournament_id: tournamentId, participants: participantsList, total_amount: finalTotal, waiver_code: isCouponApplied ? couponCode : null };
  //     const data = await createOrder(token, orderData);
  //     console.log(data);
  //     const options = {
  //       key: "rzp_live_Rg1tGOHDjKtxco",
  //       amount: Math.round(finalTotal * 100),
  //       currency: "INR",
  //       name: "Tournament Portal",
  //       order_id: data.order.razorpay_order_id,
  //       handler: async (response) => {
  //         const verify = await verifyPayment(token, response);
  //         if (verify.status === "success") navigate("/success");
  //       },
  //       prefill: { name: user?.name, email: user?.email },
  //       theme: { color: "#0F172A" }
  //     };
  //     const rzp = new window.Razorpay(options);
  //     rzp.open();
  //   } catch (err) {
  //     setStatusMessage({ text: "Payment Error", type: "danger" });
  //   } finally { setIsSubmitting(false); }
  // };

  return (
    <div className="min-vh-100 pb-5" style={{ backgroundColor: "#F9FAFB", fontFamily: "'Inter', sans-serif" }}>
      
      {/* REDUCED HEIGHT TITLE HEADER */}
      <div style={{ background: "linear-gradient(135deg, #0F172A 0%, #1E293B 100%)", padding: "1.2rem 0" }}>
        <div className="container" style={{ maxWidth: "1100px" }}>
          <div className="row align-items-center">
            <div className="col-lg-8">
              <nav className="d-flex align-items-center mb-1 text-white-50 small fw-medium">
                <span className="cursor-pointer" onClick={() => navigate("/tournaments")}>Tournaments</span>
                <FaChevronRight className="mx-2" size={8} />
                <span className="text-white">Registration Portal</span>
              </nav>
              <h1 className="h4 fw-bold text-white mb-0">{tournament?.name}</h1>
            </div>
            <div className="col-lg-4 mt-3 mt-lg-0 text-lg-end">
              <div className="d-inline-flex bg-white bg-opacity-10 border border-white border-opacity-20 rounded-3 p-2 text-white shadow-lg" style={{ backdropFilter: "blur(12px)" }}>
                <div className="px-3 border-end border-white border-opacity-10 text-center">
                  <div className="small opacity-75 text-uppercase fw-bold mb-0" style={{ fontSize: '0.6rem' }}>Entry Fee</div>
                  <div className="fw-bold">₹{entryFee}</div>
                </div>
                <div className="px-3 text-center">
                  <div className="small opacity-75 text-uppercase fw-bold mb-0" style={{ fontSize: '0.6rem' }}>{additionalFeeLabel}</div>
                  <div className="fw-bold">₹{additionalFee}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container" style={{ maxWidth: "1100px", marginTop: "1.5rem" }}>
        
        {/* INFO CARD */}
        <div className="card border-0 shadow-sm rounded-4 mb-4">
          <div className="card-body d-flex align-items-center p-3">
            <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-3">
              <FaInfoCircle className="text-primary" size={20} />
            </div>
            <div className="small">
              <span className="fw-bold text-dark d-block">CDCA Member Verification</span>
              <span className="text-muted"> Apply your waiver code if the CDCA fee <strong>₹{additionalFee}</strong> has already been paid for this year. Contact <b>+91 95786 86886</b>.</span>
            </div>
          </div>
        </div>

        {statusMessage.text && (
          <div className={`alert alert-${statusMessage.type} border-0 shadow-sm py-2 px-3 small mb-4 fw-medium animate__animated animate__fadeIn`}>
            {statusMessage.text}
          </div>
        )}

        <div className="row g-4">
          {/* LEFT: ORIGINAL FORM DESIGN */}
          <div className="col-lg-7">
            <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
              <div className="card-header bg-white py-3 px-4 border-bottom-0 mt-2">
                <div className="d-flex align-items-center">
                  <FaListAlt className="text-primary me-2" />
                  <h5 className="mb-0 fw-bold text-dark">Add Participant</h5>
                </div>
              </div>
              <div className="card-body p-4 pt-0">
                <form onSubmit={handleAddParticipant}>
                  <div className="row g-4">
                    <div className="col-md-8">
                      <label className="form-label small fw-bold text-muted text-uppercase tracking-wider" style={{ fontSize: '0.65rem' }}>Full Player Name</label>
                      <input className="form-control border-light-subtle shadow-none py-2" name="first_name" value={participant.first_name} onChange={handleInput} required placeholder="Enter name" />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small fw-bold text-muted text-uppercase tracking-wider" style={{ fontSize: '0.65rem' }}>Initial</label>
                      <input className="form-control border-light-subtle shadow-none py-2" name="initial" value={participant.initial} onChange={handleInput} required placeholder="Initial" />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-bold text-muted text-uppercase tracking-wider" style={{ fontSize: '0.65rem' }}>Date of Birth</label>
                      <input type="date" className="form-control border-light-subtle shadow-none py-2" name="dob" value={participant.dob} onChange={handleInput} required />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-bold text-muted text-uppercase tracking-wider" style={{ fontSize: '0.65rem' }}>Playing Category</label>
                      <select className="form-select border-light-subtle shadow-none py-2" name="category" value={participant.category} onChange={handleInput} required>
                        <option value="">Select Category</option>
                        {categories.map((cat, idx) => (
                          <option key={idx} value={cat.category_name}>{cat.category_name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-12 text-end">
                      <button type="submit" className="btn btn-primary px-4 py-2 fw-bold rounded-3 shadow-sm">
                        Add to participants
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* RIGHT: ORIGINAL CHECKOUT DESIGN */}
          <div className="col-lg-5">
            <div className="card border-0 shadow-sm rounded-4 border-top border-primary border-4">
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h6 className="mb-0 fw-bold text-dark"><FaUsers className="me-2 text-primary" /> Registration Roster</h6>
                  <span className="badge bg-light text-primary border rounded-pill px-3 py-1 small">{participantsList.length}</span>
                </div>

                <div className="mb-4" style={{ maxHeight: "180px", overflowY: "auto" }}>
                  {participantsList.length === 0 ? (
                    <div className="text-center py-4 bg-light rounded-4 text-muted small border-dashed border">
                      No players added yet.
                    </div>
                  ) : (
                    <div className="list-group list-group-flush rounded-4 overflow-hidden border">
                      {participantsList.map((p, i) => (
                        <div key={i} className="list-group-item px-3 py-2 d-flex justify-content-between align-items-center bg-white">
                          <div>
                            <span className="fw-bold d-block small text-dark">{p.first_name} {p.initial}</span>
                            <span className="text-muted extra-small" style={{ fontSize: '0.7rem' }}>{p.category}</span>
                          </div>
                          <button className="btn btn-link text-danger p-0 border-0" onClick={() => handleRemoveParticipant(i)}>
                            <FaTrash size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="bg-light p-3 rounded-4 mb-4 border border-light-subtle">
                  <label className="form-label fw-bold text-muted mb-2" style={{ fontSize: '0.6rem', letterSpacing: '1px' }}>APPLY WAIVER CODE</label>
                  <div className="input-group input-group-sm">
                    <span className="input-group-text bg-white border-end-0">
                      <FaTicketAlt className={isCouponApplied ? "text-success" : "text-muted"} size={12} />
                    </span>
                    <input 
                      type="text" 
                      className={`form-control border-start-0 ps-0 shadow-none ${isCouponApplied ? "text-success fw-bold" : ""}`} 
                      placeholder="Enter Code" 
                      value={couponCode} 
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      disabled={isCouponApplied || isValidating}
                    />
                    {isCouponApplied ? (
                      <button className="btn btn-outline-danger fw-bold px-3" type="button" onClick={handleRemoveWaiver}>Remove</button>
                    ) : (
                      <button className="btn btn-dark fw-bold px-3" type="button" onClick={handleApplyDiscount} disabled={isValidating || !couponCode || participantsList.length === 0}>
                        {isValidating ? <span className="spinner-border spinner-border-sm"></span> : "Apply"}
                      </button>
                    )}
                  </div>
                </div>

                <div className="pt-2">
                  <div className="d-flex justify-content-between mb-2 small text-muted">
                    <span>Registration Subtotal</span>
                    <span>₹{rawSubtotal.toFixed(2)}</span>
                  </div>
                  {isCouponApplied && (
                    <div className="d-flex justify-content-between mb-2 small text-success fw-bold">
                      <span>Waiver Discount (Applied)</span>
                      <span>-₹{discountAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="d-flex justify-content-between align-items-center pt-3 border-top mt-2">
                    <span className="fw-bold text-dark fs-6">Total Payable</span>
                    <span className="h3 mb-0 fw-black text-primary">₹{finalTotal.toFixed(2)}</span>
                  </div>
                </div>

                <button 
                  className="btn btn-primary w-100 py-3 mt-4 fw-bold shadow-sm rounded-3 d-flex align-items-center justify-content-center" 
                  disabled={isSubmitting || participantsList.length === 0} 
                  onClick={handlePayment}
                >
                  <FaShieldAlt className="me-2" /> {isSubmitting ? "Processing..." : `Secure Checkout`}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}